import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react';
import Link from 'next/link';
import { useQuery } from '@tanstack/react-query';
import { connectorDescriptionKey, fetchConnectors } from '../../../lib/connectors';
import { HOME3_CANVAS_LABEL_KEY, type Home3Destination, type Home3Event, type Home3ReadState } from '../../../lib/home3-feed';
import { liveChipShown, panelHeadline } from '../../../lib/home3-panel-row';
import { BRIEF_AUTO_OPEN_DELAY_MS, briefAutoOpenDue, localDayStamp, markBriefSeenToday, readBriefSeenDay } from '../../../lib/brief-auto-open';
import { outreachSiblings } from '../../../lib/outreach-thread-stepper';
import { calendarSlug } from '../../../lib/home-map/channels';
import { useMapWorld } from '../../../lib/home-map/use-map-world';
import { BadgeFill, arrivalPulseMs, arrivalPulseScale, flowCategories, type FlowCategory } from '../../../lib/home-map/phone-flow';
import * as L from '../../../lib/home-map/phone-layout';
import type { PhoneCardAnchorMode } from '../../../lib/home-map/phone-popover';
import { identityZoom, panZoom, pinchZoom, zoomTapSlop, zoomTransform, type PhoneZoom, type ZoomPoint } from '../../../lib/home-map/phone-zoom';
import {
  agentCardContent,
  chipCardAction,
  chipCardContent,
  connectMoreCardContent,
  foldedCardContent,
  maestroCardContent,
  type PhoneMapCard,
  type PhoneMapCardContent,
} from '../../../lib/home-map/phone-map-cards';
import { queryKeys } from '../../../lib/query-client';
import { useSiteInfo } from '../../../lib/site-info';
import { useAgentName } from '../../../lib/use-agent-name';
import { useBasePath } from '../../../lib/use-base-path';
import { useProductName } from '../../../lib/use-brand';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { ACTIVITY_MAP_BI_PAGE, ACTIVITY_MAP_MAESTRO_ENTITY, activityMapAgentEntity, activityMapRailName } from '../../../lib/activity-map-bi';
import { useMaestroBrief } from '../../../lib/use-maestro-brief';
import { useOpenSideChat } from '../../../lib/use-open-side-chat';
import { useTranslation } from '../../../lib/i18n';
import { useRecipient } from '../../settings/shared/settings-queries';
import { AgentDot } from '../../AgentDot';
import { MaestroMark } from '../MaestroMark';
import { Typography } from '../ui';
import { HOME3_ENTRY_ROUTE } from './Home3EntryPoints';
import { Home3CategoryArt, type Home3Messenger } from './Home3Categories';
import {
  ChannelGlyph,
  ChipMark,
  FoldedList,
  PhoneMapBriefModal,
  PhoneMapCardDialog,
  PlusPlate,
  ToolLogo,
} from './Home3PhoneMapCards';
import { DashboardVisibleContext } from '../DashboardView/dashboard-visible';
import { Home3PhoneMapFlow, useReducedMotion } from './Home3PhoneMapFlow';
import { Home3PhoneLiveSheet } from './Home3PhoneLiveSheet';
import liveStyles from './Home3PhoneLiveSheet.module.css';
import { useHome3EventOpener } from './use-home3-event-opener';
import styles from './Home3PhoneMap.module.css';

/** The band's own height in frame units — what the band's aspect ratio is built from. */
const BAND_H = L.bandBottom - L.bandTop;

/** Each rail row's category, top to bottom; the brief is its own (`null`). `messages` is the Outreach category. */
const RAIL_DESTINATION: Record<L.Rail, Home3Destination | null> = {
  brief: null,
  tasks: 'tasks',
  routines: 'routines',
  goals: 'goals',
  messages: 'outreach',
  apps: 'apps',
  files: 'files',
};

/** One chip in a tray: a channel in, or a connected tool. */
type Chip = { slug: string; label: string; logoUrl?: string };

/**
 * **The phone activity map** — the octopus, as both phones draw it (Figma `G2e3uWY8iBZd4g0pFJ5ruk`, node
 * 2046:117940), for mobile web under the 720px narrow breakpoint. The desktop draws its own canvas (`HomeMap`
 * in `Home3Body`'s canvas branch); this is the phone frame's composition, not a squeezed canvas.
 *
 * ## Where things go is the layout module's question, not this file's
 *
 * Every position is `lib/home-map/phone-layout.ts` — a 1:1 port of iOS/Android `ActivityMapLayout`, pinned to
 * the same `native-apps/shared/activity-map-layout/contract.json` both phones are. This file is plumbing.
 *
 * ## The layers, one geometry
 *
 * The band keeps the frame's aspect ratio (375 : 507) at the container's width, so it never depends on the
 * viewport's height (`100dvh` moves with the browser's own toolbars). Bottom to top:
 *
 * - the WIRES are one `<svg>` in screen pixels, laid out through `scaleTo` exactly as the phones' canvases are
 *   (`wireOnScreen` / `clusterWireOnScreen`), so their strokes stay the frame's 1 and 2 at any width;
 * - the flow's OUT layer (rail sparks and outputs — `Home3PhoneMapFlow`, modelled by `lib/home-map/phone-flow.ts`
 *   with both phones): a `<canvas>` in screen pixels on the same `scaleTo`, UNDER the board, so an output slides into
 *   its rail icon's art and is hidden by it;
 * - everything else — the headline, the Maestro, the agents, the trays and the rail — is laid out in FRAME
 *   units on a board scaled to the width as one piece, so the 40 art boxes and the type keep their proportions;
 * - the trays' wire ANCHOR DOTS (`TrayAnchors`), in screen pixels, over the trays they sit on the edge of;
 * - the flow's IN layer (the incoming tags), over all of it.
 *
 * The flow is paused while a card or the Brief modal is open, and made up: an output's wire is random, never the
 * rail's counts.
 *
 * ## Pinch to zoom (user decision 2026-11-03)
 *
 * Two fingers zoom the whole composition 1×–2× about their centroid (`lib/home-map/phone-zoom.ts`); zoomed, one finger
 * pans it, never past the band's edges. It is a CSS transform on the sizer, written straight to the element, so the
 * board is not laid out again and the flow's sim never rebuilds per pinch frame — its canvases zoom with the board. At
 * 1× the band is `touch-action: pan-y`, so one finger still scrolls the page and the browser does not page-zoom a pinch
 * over the map; zoomed it is `touch-action: none`. A press that became a pinch or a pan does not also open what it
 * started on. The zoom resets on each appearance of the map (and on a view switch, which unmounts it), and is off —
 * and reset — while the Recent activity sheet is open.
 *
 * ## The trays never expand
 *
 * Three chips and a "+N" each, always: the "+N" opens the FOLDED card — the chips it hides, listed, and "Manage"
 * into the Connectors tab that lists them all — as the desktop canvas's "+N" does (the user's decision,
 * superseding the expanded-tray frame). So the composition never leaves the frame, and the band never scrolls.
 *
 * The TOOLS tray then ends in a fixed "+" (`hasAddButton`) — there even with no tool connected, when it is the
 * whole tray — that opens the "Connect more" card, as the desktop canvas's connect-more node does. The channels
 * tray has none. The "+" is the tray's last round button, so the tools wire's anchor can land on it.
 *
 * ## Doors, as the desktop canvas's entry points are
 *
 * A category row is a link to that category's page, and `onOpen` runs on the way out (BI, and the category's
 * badge clears). The phones open a drawer from those rows instead — a divergence the product owner has not
 * settled yet.
 *
 * ## The cards (Figma 2045:107961, 2048:122213, 2048:125261)
 *
 * The Brief row opens the Brief modal (the desktop canvas's brief content in the DS modal shell; with no brief
 * yet, the narrow board's own seeded question instead). A dot — an agent or the Maestro — opens its agent card,
 * a chip its connector card, a tray's "+N" its folded card, the tools tray's "+" the "Connect more" card. Each opens
 * NEXT TO what was pressed (`lib/home-map/phone-popover.ts`: beside a dot, above — else below — a chip; user decision
 * 2026-11-03), measured from the trigger's on-screen rect, so it follows the page's scroll and the pinch zoom. Each is
 * closed by ✕, Escape or a press outside, and only one is ever open: they are one piece of state (`PhoneMapCard`).
 * Mirrors iOS/Android `ActivityMapScreen`.
 *
 * ## Live Updates (Figma 2048:119655)
 *
 * Under the headline, while there is anything unseen, the Live Updates chip opens the Recent activity sheet: the
 * very rows the headline counts, as the desktop canvas's activity panel lists them (`Home3PhoneLiveSheet`). It is
 * one more piece of the one-card state, so it closes any card and a card closes it. A row's link opens what the
 * board's row opens (`useHome3EventOpener`, held here so its drill-ins outlive the sheet).
 *
 * ## The Brief opens by itself, once a day
 *
 * Five seconds after the map has settled (its counts are in) and there is a brief to show, the Brief modal opens by
 * itself — once a local calendar day, the rule desktop and both phones share (`lib/brief-auto-open.ts`). It never
 * opens over another card or the Recent activity sheet, and a Brief opened by hand spends the day's showing too.
 *
 * ## BI: the Activity Map's names, 1:1 with the phones
 *
 * Every press that opens a card, and every card's action, logs its name from
 * `native-apps/shared/activity-map-bi/contract.json` on `ACTIVITY_MAP_BI_PAGE`; closing a card logs nothing.
 */
export function Home3PhoneMap({
  agents,
  counts,
  updates,
  events,
  agentNames,
  spaceNames,
  maestroName,
  senders,
  messenger,
  onOpen,
  summary,
}: {
  /** The team, in roster order; the ring shows the first `maxAgentDots`. Role and description fill its card. */
  agents: { id: string; name: string; title?: string; description?: string }[];
  /** What each category wears on the rail. Null until the reads and the stamps are in — then no badge at all. */
  counts: Record<Home3Destination, number> | null;
  /** The headline's number. Null until settled — then no headline, as on the desktop canvas. */
  updates: number | null;
  /** The unseen rows the headline counts, oldest first — the Recent activity sheet lists them. */
  events: Home3Event[];
  agentNames: Record<string, string>;
  spaceNames: Record<string, string>;
  maestroName: string;
  /** A message row's face, by outreach id (`Home3ActivityPanel`'s). */
  senders?: Record<string, Home3Messenger>;
  /** Whoever wrote most recently — the Outreach row wears their face, as the tiles do. */
  messenger?: Home3Messenger;
  /** A category row was pressed; its link navigates by itself. */
  onOpen: (entry: Home3Destination) => void;
  /** What the Brief modal says — the board's brief card's own inputs (`summaryNumbers`). */
  summary: { readState: Home3ReadState; closed: number; moving: number };
}) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();

  // ## The rail badges FILL from the flow (`BadgeFill`, user decision 2026-11-03)
  // Each time the map appears every badge starts empty, and an output reaching its rail icon adds one, up to the real
  // count — so a badge never moves without a visible arrival. This mounts with the map view, and a return to the home
  // (`DashboardVisibleContext`, the home stays mounted hidden under other screens) is an appearance too: a fresh fill.
  // With reduced motion no flow is drawn, so the badges show the real counts at once.
  const onScreen = useContext(DashboardVisibleContext);
  const reducedMotion = useReducedMotion();
  const [fill, setFill] = useState(() => new BadgeFill());
  const [, setFillVersion] = useState(0);
  const wasOnScreen = useRef(onScreen);
  useEffect(() => {
    if (onScreen && !wasOnScreen.current) setFill(new BadgeFill());
    wasOnScreen.current = onScreen;
  }, [onScreen]);
  useEffect(() => {
    const targets: Partial<Record<L.Rail, number>> = {};
    for (const rail of flowCategories) {
      const destination = RAIL_DESTINATION[rail];
      targets[rail] = destination && counts ? counts[destination] : 0;
    }
    fill.setTargets(targets);
    setFillVersion(fill.version);
  }, [fill, counts]);
  // An output reaching its rail icon PULSES it — its art and its badge together — as desktop pulses an entry point
  // when a tag slides in (`CanvasDemoFlow`'s `pulseArt`): every landing, the badge full or not. A scale on the art box,
  // so nothing around it moves.
  const sizerRef = useRef<HTMLDivElement | null>(null);
  const pulseRails = (landed: readonly FlowCategory[]) => {
    for (const rail of landed) {
      sizerRef.current?.querySelector<HTMLElement>(`[data-rail="${rail}"] .${styles.railArt}`)?.animate(
        [{ transform: 'scale(1)' }, { transform: `scale(${arrivalPulseScale})` }, { transform: 'scale(1)' }],
        { duration: arrivalPulseMs, easing: 'ease-out' },
      );
    }
  };
  const brief = useMaestroBrief();
  const openSideChat = useOpenSideChat();
  const agentName = useAgentName();
  const productName = useProductName();
  const hasPhone = !!useRecipient().data?.phone?.trim();
  const { channelsIn, connectors } = useMapWorld({ withSpaces: false });
  // The connectors read behind the chips (React Query shares it with `useMapWorld`) — a tool's description.
  const connectorRows = useQuery({ queryKey: queryKeys.connectors, queryFn: fetchConnectors, staleTime: 60_000 }).data?.connectors;

  // The ONE open card (or the Brief modal) — opening another replaces it — and what it opened from: a card opens next
  // to its trigger (`PhoneMapCardDialog`'s `anchor`), beside a dot or above a chip.
  const [card, setCard] = useState<PhoneMapCard | null>(null);
  const [anchor, setAnchor] = useState<{ el: HTMLElement; mode: PhoneCardAnchorMode } | null>(null);
  const closeCard = () => setCard(null);
  /** Opens `next` next to the element that was pressed. */
  const openAt = (next: PhoneMapCard, el: HTMLElement, mode: PhoneCardAnchorMode) => {
    setAnchor({ el, mode });
    setCard(next);
  };
  // What a sheet row opens — the board's own opener, mounted here so a drill-in it draws outlives the sheet.
  const opener = useHome3EventOpener({ agentNames, spaceNames });

  const channelFold = L.visibleChips(channelsIn);
  const connectorFold = L.visibleChips(connectors);
  const channelsWidth = L.trayWidth(channelFold.shown.length, channelFold.hasMore, L.hasAddButton('channels'));
  // The whole tools tray, its "+" included — the anchor guard measures it so the wire meets a real button.
  const connectorsWidth = L.trayWidth(connectorFold.shown.length, connectorFold.hasMore, L.hasAddButton('connectors'));
  const shownAgents = useMemo(() => agents.slice(0, L.maxAgentDots), [agents]);
  // The flow's one input: every channel chip's slug, folded ones included, for the incoming glyphs.
  const channelSlugs = useMemo(() => channelsIn.map((c) => c.slug), [channelsIn]);
  const angles = L.agentAngles(shownAgents.length);

  // The band's width, measured — the one input the scale needs (its height follows from the aspect ratio).
  const bandRef = useRef<HTMLDivElement | null>(null);
  const [bandW, setBandW] = useState(0);
  useLayoutEffect(() => {
    const el = bandRef.current;
    if (!el) return undefined;
    const apply = () => setBandW(el.clientWidth);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const zoom = bandW / L.frameW;
  const s = L.scaleTo(bandW, BAND_H * zoom);
  const bandH = BAND_H * zoom;

  // ## The pinch zoom (`lib/home-map/phone-zoom.ts`). The live value is a ref written straight to the sizer's
  // `transform`: a pinch re-renders nothing. Only "is it zoomed" is state — it flips the band's `touch-action`.
  const pinch = useRef<PhoneZoom>(identityZoom);
  const [zoomed, setZoomed] = useState(false);
  const applyZoom = (z: PhoneZoom) => {
    pinch.current = z;
    if (sizerRef.current) sizerRef.current.style.transform = zoomTransform(z);
    setZoomed(z.k > 1);
  };
  const resetZoom = () => applyZoom(identityZoom);
  // Every appearance of the map (a return to the home, which stays mounted hidden) starts it at 1×; a view switch
  // unmounts the map, so the next one does too.
  const wasShown = useRef(onScreen);
  useEffect(() => {
    if (onScreen && !wasShown.current) resetZoom();
    wasShown.current = onScreen;
  }, [onScreen]);
  // Off, and back to 1×, while the Recent activity sheet is open.
  const zoomOff = card?.kind === 'live';
  const zoomOffRef = useRef(zoomOff);
  zoomOffRef.current = zoomOff;
  // Before the paint the sheet opens in: it is placed on the 1× board (`liveSheetTop`), so the board is never seen
  // still zoomed under it.
  useLayoutEffect(() => {
    if (zoomOff) resetZoom();
  }, [zoomOff]);
  // A resize lays the board out again at its new width: start it over at 1×.
  useEffect(() => resetZoom(), [bandW]);
  const applyZoomRef = useRef(applyZoom);
  applyZoomRef.current = applyZoom;

  // The fingers, on the band. Native listeners: `touchmove` must be non-passive to keep the page from scrolling or
  // zooming under a pinch, and iOS Safari's own page zoom only stops at its `gesture*` events.
  useEffect(() => {
    const band = bandRef.current;
    if (!band) return undefined;
    const fingers = new Map<number, ZoomPoint>();
    type Gesture = { kind: 'pinch'; start: PhoneZoom; from: ZoomPoint; spread: number } | { kind: 'pan'; start: PhoneZoom; from: ZoomPoint };
    let gesture: Gesture | null = null;
    let pressAt: ZoomPoint | null = null;
    let moved = false;
    let eatClick = false;
    let eatTimer = 0;
    const local = (e: PointerEvent): ZoomPoint => {
      const r = band.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    // The sizer's own (untransformed) size: what the pan is clamped to.
    const size = () => ({ w: sizerRef.current?.offsetWidth ?? 0, h: sizerRef.current?.offsetHeight ?? 0 });
    const centroid = (a: ZoomPoint, b: ZoomPoint): ZoomPoint => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
    // (Re)starts the gesture from the fingers down now: two pinch, one pans while zoomed, else none (the page's).
    const rebase = () => {
      const [a, b] = [...fingers.values()];
      const start = pinch.current;
      if (a && b) gesture = { kind: 'pinch', start, from: centroid(a, b), spread: Math.hypot(a.x - b.x, a.y - b.y) };
      else if (a && start.k > 1) gesture = { kind: 'pan', start, from: a };
      else gesture = null;
    };
    const onDown = (e: PointerEvent) => {
      if (zoomOffRef.current || (e.pointerType === 'mouse' && e.button !== 0)) return;
      if (fingers.size === 0) {
        pressAt = local(e);
        moved = false;
        eatClick = false;
      }
      fingers.set(e.pointerId, local(e));
      rebase();
    };
    const onMove = (e: PointerEvent) => {
      if (!fingers.has(e.pointerId)) return;
      const p = local(e);
      fingers.set(e.pointerId, p);
      if (pressAt && Math.hypot(p.x - pressAt.x, p.y - pressAt.y) > zoomTapSlop) moved = true;
      if (!gesture) return;
      const { w, h } = size();
      if (gesture.kind === 'pinch') {
        const [a, b] = [...fingers.values()];
        if (!a || !b) return;
        moved = true;
        applyZoomRef.current(pinchZoom(gesture.start, gesture.from, gesture.spread, centroid(a, b), Math.hypot(a.x - b.x, a.y - b.y), w, h));
      } else if (moved) {
        applyZoomRef.current(panZoom(gesture.start, p.x - gesture.from.x, p.y - gesture.from.y, w, h));
      }
    };
    const onUp = (e: PointerEvent) => {
      if (!fingers.delete(e.pointerId)) return;
      // A press that pinched or panned does not also open what it started on.
      if (moved && gesture && e.type === 'pointerup') {
        eatClick = true;
        window.clearTimeout(eatTimer);
        eatTimer = window.setTimeout(() => {
          eatClick = false;
        }, 400);
      }
      rebase();
    };
    const onClick = (e: globalThis.MouseEvent) => {
      if (!eatClick) return;
      eatClick = false;
      e.preventDefault();
      e.stopPropagation();
    };
    // While two fingers are down, or the board is zoomed, the page neither scrolls nor zooms under them.
    const onTouchMove = (e: TouchEvent) => {
      if (zoomOffRef.current) return;
      if ((e.touches.length >= 2 || pinch.current.k > 1) && e.cancelable) e.preventDefault();
    };
    const noPageZoom = (e: Event) => e.preventDefault();
    band.addEventListener('pointerdown', onDown);
    band.addEventListener('pointermove', onMove);
    band.addEventListener('pointerup', onUp);
    band.addEventListener('pointercancel', onUp);
    band.addEventListener('click', onClick, true);
    band.addEventListener('touchmove', onTouchMove, { passive: false });
    for (const type of ['gesturestart', 'gesturechange', 'gestureend']) band.addEventListener(type, noPageZoom);
    return () => {
      window.clearTimeout(eatTimer);
      band.removeEventListener('pointerdown', onDown);
      band.removeEventListener('pointermove', onMove);
      band.removeEventListener('pointerup', onUp);
      band.removeEventListener('pointercancel', onUp);
      band.removeEventListener('click', onClick, true);
      band.removeEventListener('touchmove', onTouchMove);
      for (const type of ['gesturestart', 'gesturechange', 'gestureend']) band.removeEventListener(type, noPageZoom);
    };
  }, []);

  /** Frame coordinates → a position on the board (frame units, from the frame's own x 0 and the band's top). */
  const at = (x: number, y: number): CSSProperties => ({ left: x, top: y - L.bandTop });

  /** The Recent activity sheet's top in viewport pixels: the frame's y 224 on the scaled band, read when it opens. The
   *  band itself is never transformed (the pinch zooms the sizer inside it) and `zoom` is the band's WIDTH scale, so
   *  it reads the 1× board — which is what the sheet opens over: opening it resets the pinch (`zoomOff`). */
  const liveSheetTop = () => (bandRef.current?.getBoundingClientRect().top ?? 0) + (L.liveSheetTop - L.bandTop) * zoom;

  /** The Maestro's plate: its own card, beside it. */
  const openMaestro = (e: MouseEvent<HTMLElement>) => {
    logPageClickBi({ page_name: ACTIVITY_MAP_BI_PAGE, button_name: 'activity-map-agent:maestro' }, { entity: ACTIVITY_MAP_MAESTRO_ENTITY });
    openAt({ kind: 'maestro' }, e.currentTarget, 'beside');
  };

  /** An agent's dot: its card, beside it. */
  const openAgent = (id: string, el: HTMLElement) => {
    logPageClickBi({ page_name: ACTIVITY_MAP_BI_PAGE, button_name: 'activity-map-agent:open' }, { entity: activityMapAgentEntity(id) });
    openAt({ kind: 'agent', id }, el, 'beside');
  };

  /** A chip on either tray: its connector card, above it. */
  const openChip = (cluster: L.Cluster, slug: string, el: HTMLElement) => {
    logPageClickBi({ page_name: ACTIVITY_MAP_BI_PAGE, button_name: `activity-map-chip-card:${cluster}` }, { additionalFields: { slug } });
    openAt({ kind: 'chip', cluster, slug }, el, 'above');
  };

  /** A tray's "+N": its folded card, above it — a tray never expands. */
  const openFolded = (cluster: L.Cluster, el: HTMLElement) => {
    logPageClickBi({ page_name: ACTIVITY_MAP_BI_PAGE, button_name: `activity-map-tray-more:${cluster}` });
    openAt({ kind: 'folded', cluster }, el, 'above');
  };

  /** The tools tray's "+": the "Connect more" card, above it. The phones' BI name, 1:1. */
  const openConnectMore = (el: HTMLElement) => {
    logPageClickBi({ page_name: ACTIVITY_MAP_BI_PAGE, button_name: 'activity-map-connect-more' });
    openAt({ kind: 'connectMore' }, el, 'above');
  };

  /** The headline's Live Updates chip: the Recent activity sheet. */
  const openLive = () => {
    logPageClickBi({ page_name: ACTIVITY_MAP_BI_PAGE, button_name: 'activity-map-live' });
    setCard({ kind: 'live' });
  };

  /** A sheet row's link (the sheet has closed): its row's drill-in, reported as the desktop panel's row is. */
  const openRow = (event: Home3Event) => {
    logPageClickBi(
      { page_name: ACTIVITY_MAP_BI_PAGE, button_name: 'activity-map-panel:open', answer: event.destination },
      { additionalFields: { destination: event.destination } },
    );
    opener.open(event, outreachSiblings(events, opener.threadSibling));
  };

  const openBrief = () => {
    logPageClickBi({ page_name: ACTIVITY_MAP_BI_PAGE, button_name: activityMapRailName('brief') });
    // The Brief modal when there is a brief to show — opened by hand, so today's showing is spent; with none yet,
    // the narrow board's own seeded question, which spends nothing, so a brief arriving later still gets its showing.
    if (brief) {
      markBriefSeenToday();
      setCard({ kind: 'brief' });
    } else openSideChat({ kind: 'new', message: t('home.overview.summary.seed') });
  };

  // The Brief by itself, once a day (`lib/brief-auto-open.ts`): only once the map has settled and there is a brief,
  // and never over whatever card the reader has open by then.
  const cardRef = useRef(card);
  cardRef.current = card;
  const settled = updates !== null && summary.readState !== 'loading';
  const hasBrief = !!brief;
  useEffect(() => {
    const due = () => briefAutoOpenDue({ settled, hasBrief, seenDay: readBriefSeenDay(), today: localDayStamp() });
    if (!due()) return undefined;
    const timer = window.setTimeout(() => {
      if (!due() || cardRef.current !== null) return;
      markBriefSeenToday();
      setCard({ kind: 'brief' });
    }, BRIEF_AUTO_OPEN_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [settled, hasBrief]);

  // The account's own Wix sites, for the Wix card — the desktop canvas's read.
  const sites = useSiteInfo()?.sites;

  /** A tool's own Connectors-page line: its localized blurb, else the server's, never the key. */
  const toolDescription = (slug: string) => {
    const row = connectorRows?.find((c) => (calendarSlug(c)?.slug ?? c.toolkit) === slug);
    if (!row) return undefined;
    const key = connectorDescriptionKey(row);
    const localized = key ? t(key, { productName }) : '';
    return (localized && localized !== key ? localized : row.description) || undefined;
  };

  /** The open card's mark and words, or null when what it was about has gone (a roster or tray change). */
  const openCard = ((): {
    mark?: ReactNode;
    content: PhoneMapCardContent;
    testId: string;
    list?: ReactNode;
    onAction?: () => void;
    onLink?: () => void;
  } | null => {
    if (!card || card.kind === 'brief' || card.kind === 'live') return null;
    if (card.kind === 'folded') {
      // The chips the tray's "+N" hides — its tail past the first three, in tray order.
      const items = card.cluster === 'channels' ? channelsIn : connectors;
      const folded = foldedCardContent(card.cluster, items, t);
      if (!folded) return null;
      const { cluster } = card;
      // No mark: the title, "{count} more", says what the "+N" folded (the user's call).
      return {
        content: { title: folded.title, actionLabel: folded.actionLabel, href: folded.href },
        list: <FoldedList cluster={card.cluster} rows={folded.rows} more={folded.more} />,
        // The phones' BI names, 1:1.
        onAction: () => logPageClickBi({ page_name: ACTIVITY_MAP_BI_PAGE, button_name: `activity-map-folded:manage:${cluster}` }),
        testId: 'phone-map-folded-card',
      };
    }
    if (card.kind === 'connectMore') {
      return {
        mark: <PlusPlate size={L.mapCardChip} />,
        content: connectMoreCardContent(t),
        onAction: () => logPageClickBi({ page_name: ACTIVITY_MAP_BI_PAGE, button_name: 'activity-map-connect-more:connect' }),
        testId: 'phone-map-connect-more-card',
      };
    }
    if (card.kind === 'maestro') {
      return {
        mark: <MaestroMark size={L.mapCardMark} />,
        content: maestroCardContent(agentName, t),
        onAction: () => logPageClickBi({ page_name: ACTIVITY_MAP_BI_PAGE, button_name: 'activity-map-agent-card:view' }, { entity: ACTIVITY_MAP_MAESTRO_ENTITY }),
        testId: 'phone-map-agent-card',
      };
    }
    if (card.kind === 'agent') {
      const agent = agents.find((a) => a.id === card.id);
      if (!agent) return null;
      return {
        mark: <AgentDot id={agent.id} size={L.mapCardMark} />,
        content: agentCardContent(agent, t),
        onAction: () =>
          logPageClickBi({ page_name: ACTIVITY_MAP_BI_PAGE, button_name: 'activity-map-agent-card:view' }, { entity: activityMapAgentEntity(agent.id) }),
        testId: 'phone-map-agent-card',
      };
    }
    const chip = (card.cluster === 'channels' ? channelsIn : connectors).find((c) => c.slug === card.slug);
    if (!chip) return null;
    const { cluster } = card;
    // The card's action reports what it does, by the same rule `chipCardContent` labels it with (Send SMS on a connected SMS
    // card); its link is the Wix card's "View site" or the WhatsApp card's "Chat on WhatsApp".
    const action = chipCardAction(cluster, chip.slug, L.chipConnected(cluster, chip.slug, true, hasPhone));
    return {
      mark: <ChipMark cluster={cluster} chip={chip} size={L.mapCardChip} />,
      // Every chip on the map is a live one: the channels are Symphony's own, the tools only the connected.
      content: chipCardContent(cluster, chip, { connected: true, hasPhone, toolDescription: toolDescription(chip.slug), productName, sites }, t),
      onAction: () =>
        logPageClickBi({ page_name: ACTIVITY_MAP_BI_PAGE, button_name: `activity-map-chip:${cluster}`, action }, { additionalFields: { slug: chip.slug } }),
      // The Wix card's "View site", which leaves the app for the site; the WhatsApp card's "Chat on WhatsApp".
      onLink: () =>
        chip.slug === 'whatsapp'
          ? logPageClickBi({ page_name: ACTIVITY_MAP_BI_PAGE, button_name: `activity-map-chip:${cluster}`, action: 'chat' }, { additionalFields: { slug: chip.slug } })
          : logPageClickBi({ page_name: ACTIVITY_MAP_BI_PAGE, button_name: 'activity-map-chip:view-site' }, { additionalFields: { slug: 'wix' } }),
      testId: 'phone-map-connector-card',
    };
  })();

  // A card whose item has gone (a roster or tray refresh) draws nothing: clear it, so it neither pauses the flow nor blocks
  // the next card. The Brief modal and the Recent activity sheet draw from their own state.
  const cardShown = card !== null && (card.kind === 'brief' || card.kind === 'live' || openCard !== null);
  useEffect(() => {
    if (card !== null && !cardShown) setCard(null);
  }, [card, cardShown]);

  // ONE sentence for the translator, with the counted run spliced in where the language puts it, so it can be set
  // in its own weight — the desktop panel's split (`panelHeadline`, shared with `Home3ActivityPanel`). At zero it is
  // its own sentence with no number: "No new updates since your last visit".
  const headline = panelHeadline(updates, t);

  return (
    <div className={styles.root} data-testid="home3-phone-map">
      {/* The hairline between the island row and the octopus (Figma 2046:118352), edge to edge. */}
      <span className={styles.divider} aria-hidden />
      <div ref={bandRef} className={`${styles.band}${zoomed ? ` ${styles.bandZoomed}` : ''}`} data-zoomed={zoomed || undefined}>
      {bandW > 0 && (
        // The composition is geometry, not text flow: it is laid out left-to-right in every locale, as on the
        // phones. The text inside it takes its own direction (`dir="auto"`). Its `transform` is the pinch zoom's,
        // written by `applyZoom` — never set here, so a render cannot undo a pinch.
        <div ref={sizerRef} className={styles.sizer} style={{ width: bandW, height: bandH }} dir="ltr">
          <Wires s={s} width={bandW} height={bandH} agents={shownAgents.length} connectorsWidth={connectorsWidth} />
          {/* The flow's out layer under the board, its in layer over it (`Home3PhoneMapFlow`). */}
          <Home3PhoneMapFlow
            scale={s}
            width={bandW}
            height={bandH}
            agents={shownAgents.length}
            slugs={channelSlugs}
            paused={cardShown}
            fill={fill}
            onFill={() => setFillVersion(fill.version)}
            onLand={pulseRails}
          >
            <div className={styles.board} style={{ width: L.frameW, height: BAND_H, transform: `scale(${zoom})` }}>
              {headline && (
                // The headline, and under its LAST line the Live Updates chip: one column, so a headline that wraps
                // to a third line pushes the chip down rather than running under it (`liveChipGap`). It is part of
                // the board, so the pinch zooms and pans it with everything else (user decision 2026-11-03).
                <div className={styles.headlineBlock} style={{ ...at(L.headlineX, L.headlineY), width: L.headlineW, gap: L.liveChipGap }}>
                  <Typography variant="h1" color="primary" component="h2" className={styles.headline} dir="auto">
                    {headline.kind === 'none' ? (
                      // No count, so no counted run: the whole sentence in the rest's weight.
                      <span className={styles.headlineRest}>{headline.text}</span>
                    ) : (
                      <>
                        {headline.before}
                        <span>{headline.count}</span>
                        {/* The counted run ends its line when it leads the sentence ("12 new updates / since your last
                            visit"); a language that puts it later keeps one flowing sentence. */}
                        {headline.before ? null : <br />}
                        <span className={styles.headlineRest}>{headline.before ? headline.after : headline.after.trimStart()}</span>
                      </>
                    )}
                  </Typography>
                  {liveChipShown(updates) && (
                    // "● Live Updates" (Figma 2046:117940): the Recent activity sheet.
                    <button
                      type="button"
                      className={liveStyles.liveChip}
                      style={{ height: L.liveChipH }}
                      aria-haspopup="dialog"
                      onClick={openLive}
                      data-live-chip
                    >
                      <span className={liveStyles.liveDot} aria-hidden />
                      <span className={liveStyles.liveLabel} dir="auto">
                        {t('home.overview.live.chip')}
                      </span>
                    </button>
                  )}
                </div>
              )}

              {/* The Maestro's plate, dead centre of the ring — the shared mark, as the desktop canvas draws it.
                  Pressed, it opens the Maestro's own card. */}
              <button
                type="button"
                className={styles.dot}
                style={at(L.ringCx - L.maestroSize / 2, L.ringCy - L.maestroSize / 2)}
                aria-label={agentName.short || t('home.map.mainAgent')}
                aria-haspopup="dialog"
                onClick={openMaestro}
              >
                <MaestroMark size={L.maestroSize} />
              </button>

              {shownAgents.map((agent, i) => {
                const p = L.ringPoint(angles[i]);
                return (
                  <button
                    key={agent.id}
                    type="button"
                    className={styles.dot}
                    style={at(p.x - L.agentDot / 2, p.y - L.agentDot / 2)}
                    aria-label={agent.name}
                    aria-haspopup="dialog"
                    onClick={(e) => openAgent(agent.id, e.currentTarget)}
                  >
                    <AgentDot id={agent.id} size={L.agentDot} />
                  </button>
                );
              })}

              <Tray
                cluster="channels"
                chips={channelFold.shown}
                more={channelFold.more}
                trayWidth={channelsWidth}
                position={at(L.trayX('channels'), L.channelsCy - L.trayH / 2)}
                onMore={(el) => openFolded('channels', el)}
                onChip={(chip, el) => openChip('channels', chip.slug, el)}
              />
              <Tray
                cluster="connectors"
                chips={connectorFold.shown}
                more={connectorFold.more}
                trayWidth={connectorsWidth}
                position={at(L.trayX('connectors'), L.connectorsCy - L.trayH / 2)}
                onMore={(el) => openFolded('connectors', el)}
                onChip={(chip, el) => openChip('connectors', chip.slug, el)}
                onAdd={openConnectMore}
              />

              {L.rails.map((rail, i) => {
                const destination = RAIL_DESTINATION[rail];
                // The badge shows what the flow has filled it to (the real count under reduced motion) — never past
                // the real count: a count that drops shows the new number on this very render, not the old one for a
                // frame until the fill's `setTargets` effect has run (as iOS draws it).
                const count =
                  destination && counts
                    ? reducedMotion || rail === 'brief'
                      ? counts[destination]
                      : Math.min(fill.shown[rail], counts[destination])
                    : 0;
                const label = destination ? t(HOME3_CANVAS_LABEL_KEY[destination]) : t('home.overview.entry.brief');
                const body = (
                  <>
                    <span className={styles.railArt}>
                      {destination ? (
                        <Home3CategoryArt destination={destination} messenger={messenger} />
                      ) : (
                        <span className={styles.briefPlate} aria-hidden>
                          <BriefListCheck />
                        </span>
                      )}
                      {count > 0 && (
                        // Figma's Super Tiny (10/16) — a DS role the `Typography` primitive has no variant for, so it
                        // is its class; the desktop entry points' own count disc.
                        <span className={`sym-type-super-tiny-semi-bold ${styles.badge}`}>{count}</span>
                      )}
                    </span>
                    <span className={`sym-type-super-tiny-semi-bold ${styles.railLabel}`} dir="auto">
                      {label}
                    </span>
                  </>
                );
                // Placed by its ICON CENTRE (the phones' `railIconCentreOnScreen`): the board is scaled uniformly,
                // so the art and the wire's end dot (`wireEndOnScreen`) keep Figma's offset at any width.
                const position = at(L.railIconCx - L.railColumnW / 2, L.railCy(i) - L.railIconSize / 2);
                return destination ? (
                  <Link
                    key={rail}
                    className={styles.rail}
                    style={position}
                    // The bare route as `href`, the mount-prefixed URL as `as` — the desktop entry points' own door.
                    href={HOME3_ENTRY_ROUTE[destination]}
                    as={`${basePath}${HOME3_ENTRY_ROUTE[destination]}`}
                    onClick={() => onOpen(destination)}
                    data-rail={rail}
                  >
                    {body}
                  </Link>
                ) : (
                  <button
                    key={rail}
                    type="button"
                    className={styles.rail}
                    style={position}
                    onClick={openBrief}
                    aria-haspopup={brief ? 'dialog' : undefined}
                    data-rail={rail}
                  >
                    {body}
                  </button>
                );
              })}
            </div>
            {/* The trays' wire anchors, OVER the trays (the wires themselves stay under them). */}
            <TrayAnchors s={s} width={bandW} height={bandH} agents={shownAgents.length} connectorsWidth={connectorsWidth} />
          </Home3PhoneMapFlow>
        </div>
      )}
      </div>

      {card?.kind === 'live' && (
        <Home3PhoneLiveSheet
          rows={events}
          top={liveSheetTop()}
          agentNames={agentNames}
          maestroName={maestroName}
          senders={senders}
          onClose={closeCard}
          onOpenRow={openRow}
        />
      )}
      {opener.modals}
      {card?.kind === 'brief' && (
        <PhoneMapBriefModal readState={summary.readState} closed={summary.closed} moving={summary.moving} onClose={closeCard} />
      )}
      {openCard && (
        <PhoneMapCardDialog
          anchor={anchor ? { trigger: anchor.el, mode: anchor.mode, band: bandRef.current } : undefined}
          mark={openCard.mark}
          content={openCard.content}
          onClose={closeCard}
          onAction={openCard.onAction}
          onLink={openCard.onLink}
          testId={openCard.testId}
        >
          {openCard.list}
        </PhoneMapCardDialog>
      )}
    </div>
  );
}

const curvePath = (k: L.Curve) => `M${k.start.x} ${k.start.y}C${k.c1.x} ${k.c1.y} ${k.c2.x} ${k.c2.y} ${k.end.x} ${k.end.y}`;

/**
 * The ring, the wires and their end dots — one draw pass under everything else, in SCREEN pixels, as the phones'
 * canvases draw them. The trays' anchor dots are NOT here: they sit on the trays' edges and draw over the trays
 * (`TrayAnchors`). Decoration: it takes no pointer.
 */
function Wires({
  s,
  width,
  height,
  agents,
  connectorsWidth,
}: {
  s: L.Scale;
  width: number;
  height: number;
  agents: number;
  connectorsWidth: number;
}) {
  const centre = s.ringCentre();
  return (
    <svg className={styles.wires} width={width} height={height} aria-hidden focusable="false">
      <circle cx={centre.x} cy={centre.y} r={s.ringRadius()} className={styles.ring} strokeDasharray={L.ringDash.map((d) => s.size(d)).join(' ')} />
      {L.clusters.map((cluster) => {
        // A tools tray too short to reach x 157 pulls its anchor onto its last button — the "+" counts. The same rule
        // places the anchor dot (`TrayAnchors`), so the wire still leaves from it.
        const folded = cluster === 'connectors' ? connectorsWidth : null;
        const k = s.clusterWireOnScreen(cluster, agents, folded);
        return (
          <g key={cluster} className={styles.clusterWire}>
            <path d={curvePath(k)} strokeWidth={L.clusterWireStroke} />
          </g>
        );
      })}
      {L.rails.map((rail, i) => {
        const k = s.wireOnScreen(i, agents);
        return (
          <g key={rail}>
            <path d={curvePath(k)} className={styles.railWire} strokeWidth={L.wireStroke} style={{ stroke: L.railWireInk[rail] }} />
            <circle cx={k.end.x} cy={k.end.y} r={s.size(L.wireDot) / 2} style={{ fill: L.railDotInk[rail] }} />
          </g>
        );
      })}
    </svg>
  );
}

/**
 * The trays' wire anchors — each tray's dot and its inner ring, where its wire leaves it — in a small layer of their
 * own drawn AFTER both trays (user decision 2026-11-03), so the tray's fill never covers half the dot. Screen pixels,
 * on the wires' `scaleTo`. Decoration: it takes no pointer.
 */
function TrayAnchors({
  s,
  width,
  height,
  agents,
  connectorsWidth,
}: {
  s: L.Scale;
  width: number;
  height: number;
  agents: number;
  connectorsWidth: number;
}) {
  const anchor = s.size(L.anchorDot);
  const ring = s.size(L.anchorDotRing);
  return (
    <svg className={styles.wires} width={width} height={height} aria-hidden focusable="false" data-tray-anchors>
      {L.clusters.map((cluster) => {
        // A tools tray too short to reach x 157 pulls its anchor onto its last button — the "+" counts.
        const folded = cluster === 'connectors' ? connectorsWidth : null;
        const { start } = s.clusterWireOnScreen(cluster, agents, folded);
        return (
          <g key={cluster}>
            <circle cx={start.x} cy={start.y} r={anchor / 2} className={styles.anchorDot} />
            {/* An INNER ring: stroked on a circle inset by half its width, so it never grows the dot. */}
            <circle cx={start.x} cy={start.y} r={(anchor - ring) / 2} strokeWidth={ring} className={styles.anchorRing} />
          </g>
        );
      })}
    </svg>
  );
}

/**
 * A tray of round chips: up to three, then a "+N" that opens the folded card listing the rest, then — the tools
 * tray only (`hasAddButton`) — the "+" that opens the "Connect more" card.
 */
function Tray({
  cluster,
  chips,
  more,
  trayWidth,
  position,
  onMore,
  onChip,
  onAdd,
}: {
  cluster: L.Cluster;
  chips: Chip[];
  more: number;
  trayWidth: number;
  position: CSSProperties;
  /** The "+N" was pressed — the host opens the tray's folded card, above it. */
  onMore: (el: HTMLElement) => void;
  /** A chip was pressed — the host opens its connector card, above it. */
  onChip: (chip: Chip, el: HTMLElement) => void;
  /** The tools tray's "+" was pressed — the host opens the "Connect more" card, above it. */
  onAdd?: (el: HTMLElement) => void;
}) {
  const { t } = useTranslation();
  if (trayWidth <= 0) return null;
  const classes = `${styles.tray} ${cluster === 'channels' ? styles.trayChannels : styles.trayConnectors}`;
  return (
    <div className={classes} style={{ ...position, width: trayWidth }} data-tray={cluster}>
      {chips.map((chip) => (
        // Pressed, a chip opens its connector card; the card's one action goes where the chip used to (`chipHref`).
        <button
          key={chip.slug}
          type="button"
          className={`${styles.chip}${chip.slug === 'wix' ? ` ${styles.chipWix}` : ''}`}
          aria-label={chip.label}
          aria-haspopup="dialog"
          title={chip.label}
          onClick={(e) => onChip(chip, e.currentTarget)}
        >
          {cluster === 'channels' ? <ChannelGlyph slug={chip.slug} label={chip.label} /> : <ToolLogo chip={chip} />}
        </button>
      ))}
      {more > 0 && (
        <button type="button" className={styles.chip} onClick={(e) => onMore(e.currentTarget)} aria-label={t('home.map.moreHidden', { count: more })} aria-haspopup="dialog">
          {/* Figma's Small/Semi Bold 14/18 — the legacy scale's `h4`. */}
          <Typography variant="h4" color="primary" component="span">{`+${more}`}</Typography>
        </button>
      )}
      {L.hasAddButton(cluster) && onAdd && (
        // The fixed last slot, never folded: Fill(buttons)/Light with a "+" (Figma 2048:123056).
        <button type="button" className={styles.chip} onClick={(e) => onAdd(e.currentTarget)} aria-label={t('home.map.connectMore')} aria-haspopup="dialog" data-tray-add>
          <PlusPlate size={L.chip} />
        </button>
      )}
    </div>
  );
}

/**
 * The Brief rail's glyph: Figma's own `24/list-check` (2045:107975) — three lines with a check at the bottom right —
 * at its frame size, 17.35, stroke 1.5 with round caps and joins, in the plate's full ink. Not lucide's `ListCheck`,
 * whose strokes and proportions differ; the vector is the frame's, exactly.
 */
function BriefListCheck() {
  return (
    <svg className={styles.briefGlyph} viewBox="0 0 17.3478 17.3478" fill="none" aria-hidden>
      <path
        d="M7.9511 13.0109H2.16848M10.8424 13.0109L12.2881 14.4565L15.1794 11.5652M11.5652 8.67392H2.16848M11.5652 4.33696H2.16848"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
